/**
 * Portfolio selection logic for the default view.
 * Extracted for testability.
 */

export const PORTFOLIO_STORAGE_KEY = 'selectedPortfolioId';

export interface PortfolioSelectionResult {
  selectedId: string | null;
  shouldShowDropdown: boolean;
}

/**
 * Determines which portfolio should be selected and whether to show the dropdown.
 *
 * Priority: URL param > valid saved ID from localStorage > first portfolio
 */
export function getPortfolioSelection(
  portfolioIds: string[],
  urlId: string | undefined,
  savedId: string | null,
  isDefaultView: boolean,
): PortfolioSelectionResult {
  // Non-default view with URL param: use URL, no dropdown
  if (!isDefaultView && urlId) {
    const isValidUrl = portfolioIds.includes(urlId);
    return {
      selectedId: isValidUrl ? urlId : null,
      shouldShowDropdown: false,
    };
  }

  // Default view: try saved ID, then first portfolio
  if (isDefaultView) {
    const validSavedId = savedId && portfolioIds.includes(savedId) ? savedId : null;
    const effectiveId = validSavedId || portfolioIds[0] || null;

    return {
      selectedId: effectiveId,
      shouldShowDropdown: portfolioIds.length >= 2,
    };
  }

  return { selectedId: null, shouldShowDropdown: false };
}

/**
 * Checks if the current selection needs to be updated (e.g., saved ID was invalid).
 * Returns the new ID to save, or null if no update needed.
 */
export function getSelectionUpdate(portfolioIds: string[], currentSelectedId: string | null): string | null {
  // If current selection is valid, no update needed
  if (currentSelectedId && portfolioIds.includes(currentSelectedId)) {
    return null;
  }

  // Need to select first portfolio (if any exist)
  const newId = portfolioIds[0] || null;

  // Only return if it's actually different
  return newId !== currentSelectedId ? newId : null;
}
